/**
 * Skiny (white-label vzhled) portalu.
 *
 * Jeden build/image, skin se vybira za behu - poradi:
 *   1. `?skin=<id>` v URL (ulozi se do sessionStorage, hodi se na prezentace / lokalni testovani)
 *   2. `window.__APP_CONFIG__.skin` - runtime config `/config.js`, v kontejneru ho generuje
 *      `docker-entrypoint.d/40-app-config.sh` z env promenne `SKIN`
 *   3. `VITE_SKIN` z buildu (lokalni vyvoj: `VITE_SKIN=supplo yarn dev`)
 *   4. default `pecosta`
 *
 * Barvy a font dodava CSS (`skins.css`) podle atributu `data-skin` na <html>; tady jsou jen
 * veci, ktere se do CSS nevejdou (logo, nazev, favicon).
 */

export type SkinId = 'pecosta' | 'supplo';

export interface Skin {
  id: SkinId;
  /** Nazev znacky do titulku stranky, paticek apod. */
  name: string;
  /** Provozovatel - paticka, copyright. */
  companyName: string;
  /** Logo na tmavem pozadi (hlavicka). Bez hodnoty se pouzije puvodni SVG ikona pecosty. */
  logoOnDark?: string;
  /** Logo na svetlem pozadi (prihlaseni). */
  logoOnLight?: string;
  favicon?: string;
}

export const SKINS: Record<SkinId, Skin> = {
  pecosta: {
    id: 'pecosta',
    name: 'Piano nákupy',
    companyName: 'Pecosta',
  },
  supplo: {
    id: 'supplo',
    name: 'Supplo',
    companyName: 'Supplo',
    logoOnDark: '/skins/supplo/logo-white.svg',
    logoOnLight: '/skins/supplo/logo-mark.svg',
    favicon: '/skins/supplo/favicon.png',
  },
};

export const DEFAULT_SKIN: SkinId = 'pecosta';

const SESSION_KEY = 'portal.skin';

declare global {
  interface Window {
    __APP_CONFIG__?: { skin?: string };
  }
}

const isSkinId = (value: unknown): value is SkinId => typeof value === 'string' && value in SKINS;

const resolveSkinId = (): SkinId => {
  const fromUrl = new URLSearchParams(window.location.search).get('skin');
  if (isSkinId(fromUrl)) {
    sessionStorage.setItem(SESSION_KEY, fromUrl);
    return fromUrl;
  }

  const fromSession = sessionStorage.getItem(SESSION_KEY);
  if (isSkinId(fromSession)) return fromSession;

  const fromRuntime = window.__APP_CONFIG__?.skin;
  if (isSkinId(fromRuntime)) return fromRuntime;

  const fromBuild = import.meta.env.VITE_SKIN;
  if (isSkinId(fromBuild)) return fromBuild;

  return DEFAULT_SKIN;
};

export const activeSkin: Skin = SKINS[resolveSkinId()];

/** Nastavi `data-skin` na <html>, titulek a favicon. Volat pred prvnim renderem. */
export const applySkin = (skin: Skin = activeSkin): void => {
  document.documentElement.dataset.skin = skin.id;
  document.title = skin.name;

  if (skin.favicon) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = skin.favicon;
  }
};
